(function () {
  'use strict';

  const FAQ_SECTION_SELECTORS = [
    '.faq_collapsible_content',
    '.faq_custom_content_with_image',
    '.custom_build_bundle_faq',
  ];

  const DURATION = 350;
  const EASING = 'ease-in-out';

  function getFaqDetailsElements() {
    const selector = FAQ_SECTION_SELECTORS.map(
      (section) => `${section} .accordion details`
    ).join(', ');

    return document.querySelectorAll(selector);
  }

  function getAccordionContent(details) {
    const summary = details.querySelector('summary');
    if (!summary) return null;

    const content = summary.nextElementSibling;
    if (!content || !content.classList.contains('accordion__content')) return null;

    return { summary, content };
  }

  function getSiblingDetails(details) {
    const container = details.closest('.grid__item, .faq_content_with_image, .collapsible-content__grid');
    if (!container) return [];

    return Array.from(container.querySelectorAll('.accordion details')).filter(
      (item) => item !== details
    );
  }

  function setAriaExpanded(summary, isExpanded) {
    summary.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
  }

  function handleFaqImageActive(details, isActive) {
    const wrapper = details.closest('.faq_block_wrapper');
    if (!wrapper) return;

    wrapper.classList.toggle('active', isActive);
  }

  function resetContentStyles(content) {
    content.style.height = '';
    content.style.overflow = '';
    content.style.transition = '';
  }

  function closeDetailsAnimated(details, content, summary) {
    return new Promise((resolve) => {
      if (!details.hasAttribute('open')) {
        resolve();
        return;
      }

      const height = content.scrollHeight;
      content.style.overflow = 'hidden';
      content.style.height = `${height}px`;
      content.style.transition = `height ${DURATION}ms ${EASING}`;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          content.style.height = '0px';
        });
      });

      const onEnd = (event) => {
        if (event.propertyName !== 'height') return;

        content.removeEventListener('transitionend', onEnd);
        details.removeAttribute('open');
        resetContentStyles(content);
        setAriaExpanded(summary, false);
        handleFaqImageActive(details, false);
        resolve();
      };

      content.addEventListener('transitionend', onEnd);
    });
  }

  function openDetailsAnimated(details, content, summary) {
    details.setAttribute('open', '');
    setAriaExpanded(summary, true);
    handleFaqImageActive(details, true);

    const height = content.scrollHeight;
    content.style.overflow = 'hidden';
    content.style.height = '0px';
    content.style.transition = `height ${DURATION}ms ${EASING}`;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        content.style.height = `${height}px`;
      });
    });

    const onEnd = (event) => {
      if (event.propertyName !== 'height') return;

      content.removeEventListener('transitionend', onEnd);
      resetContentStyles(content);
    };

    content.addEventListener('transitionend', onEnd);
  }

  function closeDetailsInstant(details, content, summary) {
    details.removeAttribute('open');
    resetContentStyles(content);
    setAriaExpanded(summary, false);
    handleFaqImageActive(details, false);
  }

  function openDetailsInstant(details, content, summary) {
    details.setAttribute('open', '');
    resetContentStyles(content);
    setAriaExpanded(summary, true);
    handleFaqImageActive(details, true);
  }

  function initSmoothFaqAccordions() {
    const detailsElements = getFaqDetailsElements();
    if (!detailsElements.length) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    detailsElements.forEach((details) => {
      if (details.dataset.smoothAccordion === 'true') return;

      const accordionParts = getAccordionContent(details);
      if (!accordionParts) return;

      const { summary, content } = accordionParts;
      details.dataset.smoothAccordion = 'true';

      if (details.hasAttribute('open')) {
        setAriaExpanded(summary, true);
        handleFaqImageActive(details, true);
      }

      summary.addEventListener('click', (event) => {
        event.preventDefault();

        const isOpen = details.hasAttribute('open');
        const siblings = getSiblingDetails(details);

        if (prefersReducedMotion) {
          if (isOpen) {
            closeDetailsInstant(details, content, summary);
            return;
          }

          siblings.forEach((sibling) => {
            const siblingParts = getAccordionContent(sibling);
            if (siblingParts) {
              closeDetailsInstant(sibling, siblingParts.content, siblingParts.summary);
            }
          });

          openDetailsInstant(details, content, summary);
          return;
        }

        if (isOpen) {
          closeDetailsAnimated(details, content, summary);
          return;
        }

        const openSiblings = siblings.filter((sibling) => sibling.hasAttribute('open'));

        Promise.all(
          openSiblings.map((sibling) => {
            const siblingParts = getAccordionContent(sibling);
            if (!siblingParts) {
              sibling.removeAttribute('open');
              handleFaqImageActive(sibling, false);
              return Promise.resolve();
            }

            return closeDetailsAnimated(
              sibling,
              siblingParts.content,
              siblingParts.summary
            );
          })
        ).then(() => {
          openDetailsAnimated(details, content, summary);
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSmoothFaqAccordions);
  } else {
    initSmoothFaqAccordions();
  }
})();
